/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import "./css/StaffPage.css";
import {
  registerStaff,
  getAllStaff,
  updateStaff,
  deleteStaff,
  toggleStaffStatus,
  setBaseSalary,
} from "../api/staffApi";
import { uploadFile, deleteFile } from "../api/fileApi";
import { getOrganization } from "../api/orgApi";
import { getZonesByOrg } from "../api/zoneApi";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiX,
  FiUser,
  FiMail,
  FiLock,
  FiBriefcase,
  FiShield,
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiEyeOff,
  FiPower,
  FiMapPin,
  FiHome,
  FiUpload,
  FiFileText,
} from "react-icons/fi";
import AccessControl from "../components/AccessControl";
import { BsCurrencyRupee } from "react-icons/bs";

const LIMIT = 10;

// ── Role meta (all roles in the system) ───────────────────────────────────
const ALL_ROLE_META = [
  {
    value: "SUPERADMIN",
    label: "Super Admin",
    color: "#3C3489",
    bg: "#EEEDFE",
  },
  { value: "ADMIN", label: "Admin", color: "#0C447C", bg: "#E6F1FB" },
  { value: "HR", label: "HR", color: "#633806", bg: "#FAEEDA" },
  {
    value: "NSM",
    label: "NSM",
    color: "#3C3489",
    bg: "#EEEDFE",
    zoneType: "NATIONAL",
  },
  {
    value: "RSM",
    label: "RSM",
    color: "#0C447C",
    bg: "#E6F1FB",
    zoneType: "REGIONAL",
  },
  {
    value: "ASM",
    label: "ASM",
    color: "#085041",
    bg: "#E1F5EE",
    zoneType: "AREA",
  },
  {
    value: "TSO",
    label: "TSO",
    color: "#633806",
    bg: "#FAEEDA",
    zoneType: "TERRITORY",
  },
  { value: "MARKETING", label: "Marketing", color: "#72243E", bg: "#FBEAF0" },
  { value: "ACCOUNTS", label: "Accounts", color: "#444441", bg: "#F1EFE8" },
  { value: "STAFF", label: "Staff", color: "#085041", bg: "#E1F5EE" },
  { value: "AGENT", label: "Agent", color: "#8A4B08", bg: "#FDEFE0" },
  { value: "BDE", label: "BDE", color: "#72243E", bg: "#FBEAF0" },
];

// ── Who can create whom (mirrors backend CAN_CREATE) ─────────────────────
const CAN_CREATE = {
  SUPERADMIN: ["ADMIN"],
  ADMIN: ["HR", "NSM", "RSM", "ASM", "TSO", "MARKETING", "ACCOUNTS", "STAFF", "AGENT", "BDE"],
  HR: ["NSM", "RSM", "ASM", "TSO", "MARKETING", "ACCOUNTS", "STAFF", "HR", "AGENT", "BDE"],
  NSM: ["RSM", "ASM", "TSO", "STAFF", "AGENT"],
  RSM: ["ASM", "TSO", "STAFF", "AGENT"],
  ASM: ["TSO", "STAFF", "AGENT"],
  TSO: [],
  MARKETING: [],
  ACCOUNTS: [],
  STAFF: [],
  AGENT: [],
  BDE: [],
};

// Zone type required per role
const ROLE_ZONE_TYPE = {
  NSM: "NATIONAL",
  RSM: "REGIONAL",
  ASM: "AREA",
  TSO: "TERRITORY",
};

// Zone role hints
const ZONE_ROLE_HINT = {
  NSM: "NSM manages a National zone",
  RSM: "RSM manages a Regional zone",
  ASM: "ASM manages an Area zone",
  TSO: "TSO manages a Territory zone",
};

const DEPARTMENTS = [
  "Operations",
  "Support",
  "Finance",
  "Marketing",
  "HR",
  "IT",
  "Management",
  "Head Office",
];

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra & Nagar Haveli",
  "Daman & Diu",
  "Delhi",
  "Jammu & Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

// ── Roles accessible for sidebar (those who can log in to admin panel) ───
const ADMIN_PANEL_ROLES = [
  "SUPERADMIN",
  "ADMIN",
  "HR",
  "NSM",
  "RSM",
  "ASM",
  "TSO",
  "MARKETING",
  "ACCOUNTS",
  "STAFF",
  "AGENT",
  "BDE",
];

export default function StaffPage() {
  const adminData = localStorage.getItem("admin");
  const userData = adminData ? JSON.parse(adminData) : null;
  const myRole = userData?.role || "";

  // Roles this logged-in user can create
  const creatableRoles = (CAN_CREATE[myRole] || [])
    .map((v) => ALL_ROLE_META.find((r) => r.value === v))
    .filter(Boolean);

  const canCreateAny = creatableRoles.length > 0;

  const [organizations, setOrganizations] = useState([]);
  const [zones, setZones] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    superadmin: 0,
    admins: 0,
    hr: 0,
    nsm: 0,
    rsm: 0,
    asm: 0,
    tso: 0,
    marketing: 0,
    accounts: 0,
    staff: 0,
    active: 0,
    inactive: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState({
    show: false,
    id: null,
    action: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // ── Base Salary modal ─────────────────────────────────────────────────
  const [salaryModal, setSalaryModal] = useState(null); // staff object
  const [salaryInput, setSalaryInput] = useState("");
  const [savingSalary, setSavingSalary] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [docUploading, setDocUploading] = useState({
    aadhaar: false,
    pan: false,
    photo: false,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterDept, setFilterDept] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const debounceRef = useRef(null);

  const emptyForm = {
    name: "",
    email: "",
    password: "",
    phone: "",
    department: "Operations",
    role: creatableRoles[0]?.value || "STAFF",
    orgId: "",
    zoneId: "",
    isActive: true,
    address: { street: "", city: "", state: "", pincode: "" },
    documents: {
      aadhaar: { url: "", publicId: "" },
      pan: { url: "", publicId: "" },
      photo: { url: "", publicId: "" },
    },
  };
  const [formData, setFormData] = useState(emptyForm);

  // ── Toast ─────────────────────────────────────────────────────────────
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // ── Copy a BDE's driver-invite link to clipboard ─────────────────────────
  const copyInviteLink = async (staffMember) => {
    if (!staffMember.inviteCode) {
      showToast("No invite code found for this BDE", "error");
      return;
    }
    const base =
      process.env.REACT_APP_DRIVER_INVITE_BASE_URL ||
      "https://app.moovuruli.in/register";
    const link = `${base}?ref=${staffMember.inviteCode}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast("Invite link copied!");
    } catch {
      showToast(link, "success"); // clipboard blocked — show link in toast as fallback
    }
  };

  // ── Set Base Salary ───────────────────────────────────────────────────
  const handleSetBaseSalary = async () => {
    if (!salaryInput || parseFloat(salaryInput) <= 0) {
      showToast("Enter a valid salary amount", "error");
      return;
    }
    setSavingSalary(true);
    try {
      const res = await setBaseSalary(salaryModal._id, parseFloat(salaryInput));
      console.log(res,"<<< base salary set")
      if (res.success) {
        showToast(`Base salary updated for ${salaryModal.name}`);
        setSalaryModal(null);
        setSalaryInput("");
        fetchStaff(currentPage, searchTerm, filterRole, filterDept);
      } else {
        showToast(res.error || "Failed", "error");
      }
    } catch { showToast("Failed", "error"); }
    finally { setSavingSalary(false); }
  };

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchStaff = useCallback(async (page, search, role, dept) => {
    try {
      setLoading(true);
      const roleParam = role === "all" ? "" : role;
      const res = await getAllStaff({
        n_page: page,
        n_limit: LIMIT,
        c_search_term: (search || "").trim(),
        c_role: roleParam || "",
        c_department: dept || "",
      });
      if (!res.success) {
        setStaffList([]);
        return;
      }
      setStaffList(res.staff || []);
      setPagination(
        res.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNextPage: false,
          hasPrevPage: false,
        },
      );
      if (res.stats) setStats(res.stats);
    } catch {
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff(1, "", "all", "");
    fetchOrganizations();
  }, [fetchStaff]);

  const fetchOrganizations = async () => {
    try {
      const res = await getOrganization();
      if (res.success !== false) setOrganizations(res.orgs || []);
    } catch {}
  };

  const fetchZones = async (orgId) => {
    if (!orgId) {
      setZones([]);
      return;
    }
    try {
      const res = await getZonesByOrg(orgId);
      if (res.success !== false) setZones(res.zones || []);
    } catch {
      setZones([]);
    }
  };

  // Zones filtered by current role's zone type requirement
  const getFilteredZones = () => {
    const zoneType = ROLE_ZONE_TYPE[formData.role];
    if (!zoneType) return zones;
    return zones.filter((z) => z.type === zoneType);
  };

  // ── Input handlers ────────────────────────────────────────────────────
  const capitalizeFirstLetter = (str) =>
    str
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const handleNameChange = (e) => {
    const cleaned = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setFormData((f) => ({ ...f, name: capitalizeFirstLetter(cleaned) }));
    if (errors.name) setErrors((e) => ({ ...e, name: "" }));
  };

  const handleEmailChange = (e) => {
    setFormData((f) => ({ ...f, email: e.target.value.toLowerCase().trim() }));
    if (errors.email) setErrors((e) => ({ ...e, email: "" }));
  };

  const handleInputChange = (field, value) => {
    if (field === "role") {
      // When role changes, clear zone if zone type no longer matches
      setFormData((f) => {
        const newZoneType = ROLE_ZONE_TYPE[value];
        const currentZone = zones.find((z) => z.zoneId === f.zoneId);
        const shouldClear =
          newZoneType && currentZone && currentZone.type !== newZoneType;
        return { ...f, role: value, zoneId: shouldClear ? "" : f.zoneId };
      });
    } else {
      setFormData((f) => ({ ...f, [field]: value }));
    }
    if (errors[field]) setErrors((e) => ({ ...e, [field]: "" }));
  };

  const handleAddressChange = (field, value) => {
    setFormData((f) => ({ ...f, address: { ...f.address, [field]: value } }));
  };

  const handleFileUpload = async (docField, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast("File must be under 5MB", "error");
      return;
    }
    setDocUploading((prev) => ({ ...prev, [docField]: true }));
    try {
      const res = await uploadFile(file);
      if (res.success) {
        setFormData((f) => ({
          ...f,
          documents: {
            ...f.documents,
            [docField]: { url: res.url, publicId: res.public_id },
          },
        }));
        showToast("File uploaded successfully!");
      } else {
        showToast(res.error || "Upload failed!", "error");
      }
    } catch {
      showToast("Upload failed! Please try again.", "error");
    } finally {
      setDocUploading((prev) => ({ ...prev, [docField]: false }));
    }
  };

  const handleDocDelete = async (docField) => {
    const doc = formData.documents[docField];
    if (!doc?.publicId) {
      setFormData((f) => ({
        ...f,
        documents: { ...f.documents, [docField]: { url: "", publicId: "" } },
      }));
      return;
    }
    setDocUploading((prev) => ({ ...prev, [docField]: true }));
    try {
      const res = await deleteFile(doc.publicId);
      if (res.success) {
        setFormData((f) => ({
          ...f,
          documents: { ...f.documents, [docField]: { url: "", publicId: "" } },
        }));
        showToast("File deleted successfully!");
      } else {
        showToast(res.error || "Delete failed!", "error");
      }
    } catch {
      showToast("Delete failed!", "error");
    } finally {
      setDocUploading((prev) => ({ ...prev, [docField]: false }));
    }
  };

  // ── Validation ────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.name.trim()) newErrors.name = "Name is required";
    else if (formData.name.trim().length < 2)
      newErrors.name = "Min 2 characters";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email))
      newErrors.email = "Invalid email";
    if (!editingId) {
      if (!formData.password.trim())
        newErrors.password = "Password is required";
      else if (formData.password.length < 6)
        newErrors.password = "Min 6 characters";
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = "Min 6 characters";
    }
    if (!formData.department) newErrors.department = "Department required";
    if (!formData.role) newErrors.role = "Role required";

    // Zone required for NSM/RSM/ASM/TSO
    const zoneType = ROLE_ZONE_TYPE[formData.role];
    if (zoneType && !formData.zoneId) {
      newErrors.zoneId = `${formData.role} must be assigned to a ${zoneType} zone`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── CRUD ──────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast("Please fix the errors!", "error");
      return;
    }
    setSubmitting(true);
    try {
      let res;
      if (editingId) {
        const updatePayload = { ...formData };
        if (!updatePayload.password) delete updatePayload.password;
        // HR cannot change isActive — remove it from update payload
        if (myRole === "HR") delete updatePayload.isActive;
        res = await updateStaff(editingId, updatePayload);
      } else {
        // HR always creates staff as inactive
        const createPayload = {
          ...formData,
          isActive: myRole === "HR" ? false : formData.isActive,
        };
        res = await registerStaff(createPayload);
      }
      if (!res.success) {
        showToast(res.error || res.message || "Operation failed!", "error");
        return;
      }
      showToast(editingId ? "Staff updated!" : "Staff added!");
      resetForm();
      fetchStaff(currentPage, searchTerm, filterRole, filterDept);
    } catch {
      showToast("Operation failed!", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteStaff(id);
      if (!res.success) {
        showToast(res.error || "Failed to delete!", "error");
        return;
      }
      showToast("Staff deleted!");
      const newPage =
        staffList.length === 1 && currentPage > 1
          ? currentPage - 1
          : currentPage;
      setCurrentPage(newPage);
      fetchStaff(newPage, searchTerm, filterRole, filterDept);
      setShowConfirm({ show: false, id: null, action: null });
    } catch {
      showToast("Failed to delete!", "error");
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleStaffStatus(id);
      if (!res.success) {
        showToast(res.error || "Failed to update!", "error");
        return;
      }
      showToast(res.message || "Status updated!");
      fetchStaff(currentPage, searchTerm, filterRole, filterDept);
      setShowConfirm({ show: false, id: null, action: null });
    } catch {
      showToast("Failed to update!", "error");
    }
  };

  const handleEdit = (s) => {
    const docs = s.documents || {};

    // ✅ Normalize doc field — DB may have old string format or new { url, publicId } object
    const normalizeDoc = (d) => {
      if (!d) return { url: "", publicId: "" };
      if (typeof d === "string") return { url: d, publicId: "" };
      return { url: d.url || "", publicId: d.publicId || "" };
    };

    setFormData({
      name: s.name || "",
      email: s.email || "",
      password: "",
      phone: s.phone || "",
      department: s.department || "Operations",
      role: s.role || "STAFF",
      orgId: s.orgId || "",
      zoneId: s.zoneId || "",
      isActive: s.isActive !== false,
      address: s.address || { street: "", city: "", state: "", pincode: "" },
      documents: {
        aadhaar: normalizeDoc(docs.aadhaar),
        pan:     normalizeDoc(docs.pan),
        photo:   normalizeDoc(docs.photo),
      },
    });
    if (s.orgId) fetchZones(s.orgId);
    setEditingId(s._id);
    setErrors({});
    setActiveTab("basic");
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ ...emptyForm, role: creatableRoles[0]?.value || "STAFF" });
    setZones([]);
    setEditingId(null);
    setErrors({});
    setShowPassword(false);
    setShowModal(false);
    setActiveTab("basic");
    setDocUploading({ aadhaar: false, pan: false, photo: false });
  };

  // ── Search / Filter ───────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => fetchStaff(1, value, filterRole, filterDept),
      400,
    );
  };
  const handleRoleFilterChange = (role) => {
    setFilterRole(role);
    setCurrentPage(1);
    fetchStaff(1, searchTerm, role, filterDept);
  };
  const handleDeptFilterChange = (e) => {
    const dept = e.target.value;
    setFilterDept(dept);
    setCurrentPage(1);
    fetchStaff(1, searchTerm, filterRole, dept);
  };
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setCurrentPage(page);
    fetchStaff(page, searchTerm, filterRole, filterDept);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const { currentPage: cp, totalPages: tp } = pagination;
    if (tp <= 1) return [];
    const pages = [];
    if (tp <= 5) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, cp - 1);
      let end = Math.min(tp - 1, cp + 1);
      if (cp <= 3) {
        start = 2;
        end = Math.min(4, tp - 1);
      }
      if (cp >= tp - 2) {
        start = Math.max(2, tp - 3);
        end = tp - 1;
      }
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < tp - 1) pages.push("...");
      if (tp > 1) pages.push(tp);
    }
    return pages;
  };

  // ── Sub-components ────────────────────────────────────────────────────
  const RoleBadge = ({ role }) => {
    const r = ALL_ROLE_META.find((x) => x.value === role);
    if (!r) return <span className="badge badge-gray">{role}</span>;
    return (
      <span
        className="badge"
        style={{ background: r.bg, color: r.color, fontWeight: 700 }}
      >
        <FiShield size={11} style={{ marginRight: 4 }} />
        {r.label}
      </span>
    );
  };

  const StatusBadge = ({ isActive }) =>
    isActive !== false ? (
      <span className="badge badge-green">
        <FiCheckCircle size={11} style={{ marginRight: 4 }} />
        Active
      </span>
    ) : (
      <span className="badge badge-red">
        <FiXCircle size={11} style={{ marginRight: 4 }} />
        Inactive
      </span>
    );

  const DocUploadTile = ({ label, field, accept, icon }) => {
    const doc = formData.documents[field] || { url: "", publicId: "" };
    const isUploading = docUploading[field];
    const isImage =
      doc.url &&
      (doc.url.startsWith("http") || doc.url.startsWith("data:image"));
    const isPdf =
      doc.url && (doc.url.includes(".pdf") || doc.url.includes("pdf"));
    return (
      <div className="sf-doc-tile">
        <label className="sf-doc-label">
          {icon} {label}
        </label>
        {doc.url ? (
          <div className="sf-doc-preview">
            {isImage && !isPdf ? (
              <img src={doc.url} alt={label} className="sf-doc-img" />
            ) : (
              <div className="sf-doc-file-icon">
                <FiFileText size={32} color="#7C3AED" />
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginTop: 6,
                flexWrap: "wrap",
              }}
            >
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 11,
                  color: "#7C3AED",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                View File ↗
              </a>
              <button
                type="button"
                className="sf-doc-remove"
                disabled={isUploading}
                onClick={() => handleDocDelete(field)}
              >
                {isUploading ? (
                  "…"
                ) : (
                  <>
                    <FiX size={12} /> Remove
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <label
            className="sf-doc-upload-area"
            style={{
              cursor: isUploading ? "not-allowed" : "pointer",
              opacity: isUploading ? 0.6 : 1,
            }}
          >
            <input
              type="file"
              accept={accept}
              style={{ display: "none" }}
              disabled={isUploading}
              onChange={(e) => handleFileUpload(field, e.target.files[0])}
            />
            {isUploading ? (
              <>
                <div
                  className="spinner"
                  style={{
                    width: 18,
                    height: 18,
                    borderColor: "#7C3AED",
                    borderTopColor: "transparent",
                  }}
                />
                <span>Uploading…</span>
              </>
            ) : (
              <>
                <FiUpload size={20} color="#aaa" />
                <span>Click to upload</span>
              </>
            )}
            <span className="sf-doc-hint">
              Max 5MB · {accept.replace(/\./g, "").toUpperCase()}
            </span>
          </label>
        )}
      </div>
    );
  };

  // ── Derived ───────────────────────────────────────────────────────────
  const filteredZones = getFilteredZones();
  const selectedRoleMeta = ALL_ROLE_META.find((r) => r.value === formData.role);
  const requiresZone = !!ROLE_ZONE_TYPE[formData.role];

  // Can current user edit this staff row?
  const canEditStaff = (targetRole) => {
    if (myRole === "SUPERADMIN") return true;
    const myLevel = {
      SUPERADMIN: 100,
      ADMIN: 80,
      HR: 60,
      NSM: 50,
      RSM: 40,
      ASM: 30,
      TSO: 20,
      MARKETING: 10,
      ACCOUNTS: 10,
      STAFF: 10,
    };
    return (myLevel[myRole] || 0) > (myLevel[targetRole] || 0);
  };

  return (
    <div>
      <AccessControl
        allowedRoles={ADMIN_PANEL_ROLES}
        redirectTo="/suggestions"
      />

      {/* Toast */}
      {toast.show && (
        <div className={`sf-toast sf-toast-${toast.type}`}>
          <span>
            {toast.type === "success" ? (
              <FiCheckCircle size={16} />
            ) : toast.type === "warning" ? (
              <FiAlertCircle size={16} />
            ) : (
              <FiXCircle size={16} />
            )}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="sf-page-header section">
        <div>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#1a1a1a",
              margin: 0,
            }}
          >
            Staff Management
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Manage team members and roles
          </p>
        </div>
        {canCreateAny && (
          <button
            className="btn btn-success"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <FiPlus size={14} /> New Staff
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E6F1FB" }}>
            <FiUsers size={18} color="#185FA5" />
          </div>
          <div className="stat-label">Total Members</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-change up">All members</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E1F5EE" }}>
            <FiShield size={18} color="#0F6E56" />
          </div>
          <div className="stat-label">Admins</div>
          <div className="stat-value" style={{ color: "#185FA5" }}>
            {stats.admins}
          </div>
          <div className="stat-change up">Admin role</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FAEEDA" }}>
            <FiUserCheck size={18} color="#854F0B" />
          </div>
          <div className="stat-label">Field Staff</div>
          <div className="stat-value" style={{ color: "#854F0B" }}>
            {(stats.nsm || 0) +
              (stats.rsm || 0) +
              (stats.asm || 0) +
              (stats.tso || 0)}
          </div>
          <div className="stat-change neutral">NSM + RSM + ASM + TSO</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FCEBEB" }}>
            <FiUserX size={18} color="#A32D2D" />
          </div>
          <div className="stat-label">Inactive</div>
          <div className="stat-value" style={{ color: "#A32D2D" }}>
            {stats.inactive}
          </div>
          <div className="stat-change down">Disabled accounts</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card section">
        <div className="sf-toolbar">
          <div className="filter-tabs">
            {[
              "all",
              ...[
                "ADMIN",
                "HR",
                "NSM",
                "RSM",
                "ASM",
                "TSO",
                "MARKETING",
                "ACCOUNTS",
                "STAFF",
                "AGENT",
                "BDE",
              ],
            ].map((f) => (
              <button
                key={f}
                className={`filter-tab ${filterRole === f ? "active" : ""}`}
                onClick={() => handleRoleFilterChange(f)}
              >
                {f === "all" ? (
                  <>
                    <FiUsers size={12} /> All
                  </>
                ) : (
                  f
                )}
              </button>
            ))}
          </div>
          <div className="sf-toolbar-right">
            <div className="search-box">
              <FiSearch size={14} className="search-icon" />
              <input
                placeholder="Search name or email…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  className="search-clear"
                  onClick={() => {
                    setSearchTerm("");
                    fetchStaff(1, "", filterRole, filterDept);
                  }}
                >
                  <FiX size={12} />
                </button>
              )}
            </div>
            <select
              className="dept-filter"
              value={filterDept}
              onChange={handleDeptFilterChange}
            >
              <option value="">All Departments</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card section">
        {loading ? (
          <div className="sf-loading" style={{ height: "40vh" }}>
            <div
              className="spinner"
              style={{
                width: 24,
                height: 24,
                borderColor: "#1d9e75",
                borderTopColor: "transparent",
              }}
            ></div>
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>
              Loading staff…
            </p>
          </div>
        ) : staffList.length === 0 ? (
          <div className="sf-empty">
            <FiUsers size={40} color="#ddd" />
            <p>No staff found</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Zone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((s) => (
                  <tr key={s._id}>
                    <td>
                      <div className="sf-member-cell">
                        <div className="sf-avatar">
                          {s.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <div className="sf-member-name">{s.name}</div>
                          <div className="sf-member-email">{s.email}</div>
                          {s.phone && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#aaa",
                                marginTop: 2,
                              }}
                            >
                              📞 {s.phone}
                            </div>
                          )}
                          {s.address?.city && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#aaa",
                                marginTop: 2,
                              }}
                            >
                              <FiMapPin size={10} style={{ marginRight: 3 }} />
                              {s.address.city}
                              {s.address.state ? `, ${s.address.state}` : ""}
                            </div>
                          )}
                          {s.role === "BDE" && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#72243E",
                                fontWeight: 600,
                                marginTop: 2,
                              }}
                            >
                              💰 Incentive: ₹{Number(s.totalIncentive || 0).toLocaleString("en-IN")}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <RoleBadge role={s.role} />
                    </td>
                    <td>
                      <span style={{ fontSize: 12, color: "#555" }}>
                        {s.department || "—"}
                      </span>
                    </td>
                    <td>
                      {s.zoneName ? (
                        <span
                          style={{
                            fontSize: 12,
                            color: "#7C3AED",
                            fontWeight: 600,
                          }}
                        >
                          <FiMapPin size={11} style={{ marginRight: 3 }} />
                          {s.zoneName}
                        </span>
                      ) : (
                        <span style={{ color: "#ccc", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td>
                      <StatusBadge isActive={s.isActive} />
                    </td>
                    <td>
                      <div className="sf-actions">
                        {canEditStaff(s.role) && (
                          <button
                            className="sf-action-btn"
                            title="Edit"
                            onClick={() => handleEdit(s)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                        )}
                        {s.role === "BDE" && (
                          <button
                            className="sf-action-btn"
                            title="Copy driver invite link"
                            style={{ background: "#FBEAF0", color: "#72243E" }}
                            onClick={() => copyInviteLink(s)}
                          >
                            🔗
                          </button>
                        )}
                        {(myRole === "SUPERADMIN" || myRole === "ADMIN") && (
                          <button
                            className="sf-action-btn"
                            title={s.baseSalary > 0 ? `Base Salary: ₹${Number(s.baseSalary).toLocaleString("en-IN")}` : "Set Base Salary"}
                            style={{ background: s.baseSalary > 0 ? "#E1F5EE" : "#FAEEDA", color: s.baseSalary > 0 ? "#085041" : "#854F0B" }}
                            onClick={() => { setSalaryModal(s); setSalaryInput(s.baseSalary > 0 ? String(s.baseSalary) : ""); }}
                          >
                            <BsCurrencyRupee size={14} />
                          </button>
                        )}
                        {(myRole === "SUPERADMIN" || myRole === "ADMIN") && (
                          <button
                            className="sf-action-btn sf-action-toggle"
                            title={s.isActive ? "Deactivate" : "Activate"}
                            onClick={() =>
                              setShowConfirm({
                                show: true,
                                id: s._id,
                                action: "toggle",
                                currentStatus: s.isActive,
                              })
                            }
                          >
                            <FiPower size={14} />
                          </button>
                        )}
                        {myRole === "SUPERADMIN" && (
                          <button
                            className="sf-action-btn sf-action-danger"
                            title="Delete"
                            onClick={() =>
                              setShowConfirm({
                                show: true,
                                id: s._id,
                                action: "delete",
                              })
                            }
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="sf-pagination">
            <button
              className="sf-page-btn"
              disabled={!pagination.hasPrevPage}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <FiChevronLeft size={14} />
            </button>
            {getPageNumbers().map((p, i) =>
              p === "..." ? (
                <span key={i} className="sf-page-dots">
                  …
                </span>
              ) : (
                <button
                  key={i}
                  className={`sf-page-btn ${p === pagination.currentPage ? "sf-page-active" : ""}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              ),
            )}
            <button
              className="sf-page-btn"
              disabled={!pagination.hasNextPage}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <FiChevronRight size={14} />
            </button>
            <span className="sf-page-info">
              {pagination.currentPage} of {pagination.totalPages} ·{" "}
              {pagination.totalCount} total
            </span>
          </div>
        )}
      </div>

      {/* ── Staff Modal ──────────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div
            className="modal-content sf-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{editingId ? "Edit Staff" : "Add New Staff"}</h3>
              <button className="modal-close" onClick={resetForm}>
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Tabs inside form for proper flex scroll chain */}
              <div className="sf-tab-bar">
                {["basic", "address", "documents"].map((tab) => (
                  <button
                    key={tab}
                    className={`sf-tab ${activeTab === tab ? "sf-tab-active" : ""}`}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === "basic"
                      ? "Basic Info"
                      : tab === "address"
                        ? "Address"
                        : "Documents"}
                  </button>
                ))}
              </div>

              <div className="modal-body">
                {/* ── TAB: Basic Info ─────────────────────────────────── */}
                {activeTab === "basic" && (
                  <div className="sf-tab-content">
                    {/* Name */}
                    <div
                      className={`field ${errors.name ? "field-error" : ""}`}
                    >
                      <label>
                        <FiUser
                          size={13}
                          style={{ marginRight: 6, color: "#1d9e75" }}
                        />
                        Full Name
                      </label>
                      <input
                        placeholder="e.g. Ravi Kumar"
                        value={formData.name}
                        onChange={handleNameChange}
                      />
                      {errors.name && (
                        <span className="field-hint error">{errors.name}</span>
                      )}
                    </div>

                    {/* Email */}
                    <div
                      className={`field ${errors.email ? "field-error" : ""}`}
                      style={{ marginTop: 14 }}
                    >
                      <label>
                        <FiMail
                          size={13}
                          style={{ marginRight: 6, color: "#1d9e75" }}
                        />
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="e.g. ravi@moovuruli.com"
                        value={formData.email}
                        onChange={handleEmailChange}
                      />
                      {errors.email && (
                        <span className="field-hint error">{errors.email}</span>
                      )}
                    </div>

                    {/* Phone */}
                    <div
                      className={`field ${errors.phone ? "field-error" : ""}`}
                      style={{ marginTop: 14 }}
                    >
                      <label>
                        <FiUser
                          size={13}
                          style={{ marginRight: 6, color: "#1d9e75" }}
                        />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        placeholder="e.g. 9876543210"
                        maxLength={10}
                        value={formData.phone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          handleInputChange("phone", val);
                          if (val && val.length !== 10)
                            setErrors((err) => ({ ...err, phone: "Enter valid 10-digit number" }));
                          else
                            setErrors((err) => ({ ...err, phone: "" }));
                        }}
                      />
                      {errors.phone && (
                        <span className="field-hint error">{errors.phone}</span>
                      )}
                    </div>

                    {/* Password */}
                    <div
                      className={`field ${errors.password ? "field-error" : ""}`}
                      style={{ marginTop: 14 }}
                    >
                      <label>
                        <FiLock
                          size={13}
                          style={{ marginRight: 6, color: "#1d9e75" }}
                        />
                        Password{" "}
                        {editingId && (
                          <span style={{ fontSize: 11, color: "#aaa" }}>
                            (leave blank to keep current)
                          </span>
                        )}
                      </label>
                      <div className="sf-password-wrap">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder={
                            editingId
                              ? "New password (optional)"
                              : "Min 6 characters"
                          }
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="sf-eye-btn"
                          onClick={() => setShowPassword((p) => !p)}
                        >
                          {showPassword ? (
                            <FiEyeOff size={14} />
                          ) : (
                            <FiEye size={14} />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <span className="field-hint error">
                          {errors.password}
                        </span>
                      )}
                    </div>

                    {/* Department + Role */}
                    <div className="sf-two-col" style={{ marginTop: 14 }}>
                      <div
                        className={`field ${errors.department ? "field-error" : ""}`}
                      >
                        <label>
                          <FiBriefcase
                            size={13}
                            style={{ marginRight: 6, color: "#1d9e75" }}
                          />
                          Department
                        </label>
                        <select
                          value={formData.department}
                          onChange={(e) =>
                            handleInputChange("department", e.target.value)
                          }
                        >
                          {DEPARTMENTS.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                        {errors.department && (
                          <span className="field-hint error">
                            {errors.department}
                          </span>
                        )}
                      </div>

                      <div
                        className={`field ${errors.role ? "field-error" : ""}`}
                      >
                        <label>
                          <FiShield
                            size={13}
                            style={{ marginRight: 6, color: "#1d9e75" }}
                          />
                          Role
                        </label>
                        {/* HR editing someone — role is read-only */}
                        {myRole === "HR" ? (
                          // <input value={formData.role} disabled style={{ background: "#f5f5f5", color: "#999" }} />
                          <select
                            value={formData.role}
                            onChange={(e) =>
                              handleInputChange("role", e.target.value)
                            }
                          >
                            {creatableRoles.length > 0 ? (
                              creatableRoles.map((r) => (
                                <option key={r.value} value={r.value}>
                                  {r.label}
                                </option>
                              ))
                            ) : (
                              <option value={formData.role}>
                                {formData.role}
                              </option>
                            )}
                          </select>
                        ) : (
                          <select
                            value={formData.role}
                            onChange={(e) =>
                              handleInputChange("role", e.target.value)
                            }
                          >
                            {creatableRoles.length > 0 ? (
                              creatableRoles.map((r) => (
                                <option key={r.value} value={r.value}>
                                  {r.label}
                                </option>
                              ))
                            ) : (
                              <option value={formData.role}>
                                {formData.role}
                              </option>
                            )}
                          </select>
                        )}

                        {errors.role && (
                          <span className="field-hint error">
                            {errors.role}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Zone hint for field roles */}
                    {ZONE_ROLE_HINT[formData.role] && (
                      <p className="sf-role-hint" style={{ marginTop: 8 }}>
                        ℹ️ {ZONE_ROLE_HINT[formData.role]}
                      </p>
                    )}

                    {/* Organization */}
                    <div className="field" style={{ marginTop: 16 }}>
                      <label>🏢 Organization</label>
                      <select
                        value={formData.orgId}
                        onChange={(e) => {
                          handleInputChange("orgId", e.target.value);
                          handleInputChange("zoneId", "");
                          fetchZones(e.target.value);
                        }}
                      >
                        <option value="">— No Organization —</option>
                        {organizations.map((org) => (
                          <option key={org.orgId} value={org.orgId}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Zone — smart filtered by role */}
                    {formData.orgId && (
                      <div
                        className={`field ${errors.zoneId ? "field-error" : ""}`}
                        style={{ marginTop: 14 }}
                      >
                        <label>
                          <FiMapPin
                            size={13}
                            style={{ marginRight: 6, color: "#7C3AED" }}
                          />
                          Zone
                          {requiresZone && selectedRoleMeta?.zoneType && (
                            <span
                              className="sf-zone-type-badge"
                              style={{
                                background: selectedRoleMeta.bg,
                                color: selectedRoleMeta.color,
                              }}
                            >
                              Showing {selectedRoleMeta.zoneType} zones only
                            </span>
                          )}
                        </label>
                        {zones.length === 0 ? (
                          <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
                            No zones found.{" "}
                            <a
                              href={`/organizations/${formData.orgId}/zones`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: "#7C3AED" }}
                            >
                              Create zones →
                            </a>
                          </p>
                        ) : filteredZones.length === 0 ? (
                          <div className="sf-zone-empty">
                            <FiMapPin size={16} color="#ccc" />
                            <span>
                              No {ROLE_ZONE_TYPE[formData.role]} zones for this
                              org.
                            </span>
                            <a
                              href={`/organizations/${formData.orgId}/zones`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Create one →
                            </a>
                          </div>
                        ) : (
                          <select
                            value={formData.zoneId}
                            onChange={(e) =>
                              handleInputChange("zoneId", e.target.value)
                            }
                          >
                            <option value="">— No Zone —</option>
                            {filteredZones.map((z) => (
                              <option key={z.zoneId} value={z.zoneId}>
                                [{z.type}] {z.name} · {z.zoneId}
                              </option>
                            ))}
                          </select>
                        )}
                        {/* Zone info card */}
                        {formData.zoneId &&
                          (() => {
                            const selZone = zones.find(
                              (z) => z.zoneId === formData.zoneId,
                            );
                            const parentZone = selZone?.parentZoneId
                              ? zones.find(
                                  (z) => z.zoneId === selZone.parentZoneId,
                                )
                              : null;
                            if (!selZone) return null;
                            return (
                              <div
                                style={{
                                  marginTop: 10,
                                  background: "#f5f3ff",
                                  border: "1.5px solid #ddd6fe",
                                  borderRadius: 10,
                                  padding: "10px 14px",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 6,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "#7C3AED",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.06em",
                                    }}
                                  >
                                    Zone Info
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      background: "#ede9fe",
                                      color: "#6d28d9",
                                      borderRadius: 4,
                                      padding: "1px 8px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {selZone.type}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "6px 16px",
                                  }}
                                >
                                  <div>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: "#94a3b8",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Zone Name
                                    </span>
                                    <div
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: "#1e1b4b",
                                      }}
                                    >
                                      {selZone.name}
                                    </div>
                                  </div>
                                  <div>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: "#94a3b8",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Zone ID
                                    </span>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: "#7C3AED",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {selZone.zoneId}
                                    </div>
                                  </div>
                                  <div>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: "#94a3b8",
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Parent Zone
                                    </span>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#6d28d9",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {selZone.parentZoneId ? (
                                        <>
                                          <span style={{ color: "#7C3AED" }}>
                                            {selZone.parentZoneId}
                                          </span>
                                          {parentZone && (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                color: "#888",
                                                marginLeft: 6,
                                              }}
                                            >
                                              ({parentZone.name})
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <span
                                          style={{
                                            color: "#ccc",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          — Root Zone —
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {selZone.location?.city && (
                                    <div>
                                      <span
                                        style={{
                                          fontSize: 10,
                                          color: "#94a3b8",
                                          fontWeight: 600,
                                          textTransform: "uppercase",
                                        }}
                                      >
                                        Location
                                      </span>
                                      <div
                                        style={{ fontSize: 12, color: "#555" }}
                                      >
                                        {[
                                          selZone.location.city,
                                          selZone.location.state,
                                        ]
                                          .filter(Boolean)
                                          .join(", ")}
                                        {selZone.location.pincode &&
                                          ` — ${selZone.location.pincode}`}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        {errors.zoneId && (
                          <span className="field-hint error">
                            {errors.zoneId}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Active status — HR-க்கு காட்டவே கூடாது (create & edit both) */}
                    {editingId && myRole !== "HR" && (
                      <div className="field" style={{ marginTop: 16 }}>
                        <label>
                          <FiPower size={13} style={{ marginRight: 6 }} />
                          Account Status
                        </label>
                        <div
                          className="sf-toggle-switch"
                          onClick={() =>
                            handleInputChange("isActive", !formData.isActive)
                          }
                        >
                          <div
                            className={`sf-toggle-slider ${formData.isActive ? "sf-toggle-on" : ""}`}
                          >
                            <div className="sf-toggle-knob"></div>
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: formData.isActive ? "#0f6e56" : "#a32d2d",
                            }}
                          >
                            {formData.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* HR info banner — edit mode-ல் status lock ஆனது காட்டு */}
                    {editingId && myRole === "HR" && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: "10px 14px",
                          background: "#FAEEDA",
                          border: "1px solid #f0c080",
                          borderRadius: 10,
                          fontSize: 12,
                          color: "#633806",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <FiPower size={13} />
                        Account status can only be changed by{" "}
                        <strong>Admin</strong> or <strong>Super Admin</strong>.
                      </div>
                    )}
                    {/* HR create — inactive notice */}
                    {!editingId && myRole === "HR" && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: "10px 14px",
                          background: "#FCEBEB",
                          border: "1px solid #f0b0b0",
                          borderRadius: 10,
                          fontSize: 12,
                          color: "#a32d2d",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <FiPower size={13} />
                        New staff created by HR will be{" "}
                        <strong>Inactive</strong> by default. Admin must
                        activate.
                      </div>
                    )}
                  </div>
                )}

                {/* ── TAB: Address ──────────────────────────────────── */}
                {activeTab === "address" && (
                  <div className="sf-tab-content">
                    <div className="sf-section-label">
                      <FiHome size={14} /> Residential Address
                    </div>
                    <div className="field" style={{ marginTop: 14 }}>
                      <label>Street / Door No.</label>
                      <input
                        placeholder="e.g. 12/A, Gandhi Street"
                        value={formData.address.street}
                        onChange={(e) =>
                          handleAddressChange("street", e.target.value)
                        }
                      />
                    </div>
                    <div className="sf-two-col" style={{ marginTop: 14 }}>
                      <div className="field">
                        <label>City</label>
                        <input
                          placeholder="e.g. Salem"
                          value={formData.address.city}
                          onChange={(e) =>
                            handleAddressChange("city", e.target.value)
                          }
                        />
                      </div>
                      <div className="field">
                        <label>Pincode</label>
                        <input
                          placeholder="e.g. 636001"
                          maxLength={6}
                          value={formData.address.pincode}
                          onChange={(e) =>
                            handleAddressChange(
                              "pincode",
                              e.target.value.replace(/\D/g, ""),
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="field" style={{ marginTop: 14 }}>
                      <label>State</label>
                      <select
                        value={formData.address.state}
                        onChange={(e) =>
                          handleAddressChange("state", e.target.value)
                        }
                      >
                        <option value="">— Select State —</option>
                        {INDIAN_STATES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(formData.address.street || formData.address.city) && (
                      <div className="sf-address-preview">
                        <FiMapPin size={14} color="#7C3AED" />
                        <span>
                          {[
                            formData.address.street,
                            formData.address.city,
                            formData.address.state,
                            formData.address.pincode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* ── TAB: Documents ────────────────────────────────── */}
                {activeTab === "documents" && (
                  <div className="sf-tab-content">
                    <div className="sf-section-label">
                      <FiFileText size={14} /> Identity Proof & Photo
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#888",
                        marginTop: 4,
                        marginBottom: 16,
                      }}
                    >
                      Upload clear scans or photos. Max 5MB per file. Supported:
                      JPG, PNG, PDF.
                    </p>
                    <div className="sf-doc-grid">
                      <DocUploadTile
                        label="Aadhaar Card"
                        field="aadhaar"
                        accept=".jpg,.jpeg,.png,.pdf"
                        icon="🪪"
                      />
                      <DocUploadTile
                        label="PAN Card"
                        field="pan"
                        accept=".jpg,.jpeg,.png,.pdf"
                        icon="💳"
                      />
                      <DocUploadTile
                        label="Staff Photo"
                        field="photo"
                        accept=".jpg,.jpeg,.png"
                        icon="📷"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="modal-actions">
                <div className="sf-tab-nav-btns">
                  {activeTab !== "basic" && (
                    <button
                      type="button"
                      className="btn-outline-green"
                      onClick={() =>
                        setActiveTab(
                          activeTab === "documents" ? "address" : "basic",
                        )
                      }
                    >
                      ← Back
                    </button>
                  )}
                  {activeTab !== "documents" && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() =>
                        setActiveTab(
                          activeTab === "basic" ? "address" : "documents",
                        )
                      }
                    >
                      Next →
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    className="btn-outline-green"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="spinner"></div>
                        {editingId ? "Updating…" : "Creating…"}
                      </>
                    ) : editingId ? (
                      <>
                        <FiEdit2 size={14} /> Update Staff
                      </>
                    ) : (
                      <>
                        <FiPlus size={14} /> Create Staff
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm.show && (
        <div
          className="modal-overlay"
          onClick={() =>
            setShowConfirm({ show: false, id: null, action: null })
          }
        >
          <div
            className="modal-content"
            style={{
              height: "auto",
              minHeight: "auto",
              maxWidth: 420,
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-body" style={{ padding: "40px 30px" }}>
              <div
                className="sf-confirm-icon"
                style={{
                  background:
                    showConfirm.action === "delete"
                      ? "#FCEBEB"
                      : showConfirm.currentStatus
                        ? "#FCEBEB"
                        : "#E1F5EE",
                  color:
                    showConfirm.action === "delete"
                      ? "#A32D2D"
                      : showConfirm.currentStatus
                        ? "#A32D2D"
                        : "#0F6E56",
                }}
              >
                {showConfirm.action === "delete" ? (
                  <FiTrash2 size={32} />
                ) : (
                  <FiPower size={32} />
                )}
              </div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginTop: 16,
                  color: "#1a1a1a",
                }}
              >
                {showConfirm.action === "delete"
                  ? "Delete this staff?"
                  : showConfirm.currentStatus
                    ? "Deactivate this staff?"
                    : "Activate this staff?"}
              </h3>
              <p style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
                {showConfirm.action === "delete"
                  ? "This action cannot be undone."
                  : showConfirm.currentStatus
                    ? "Staff will be unable to login."
                    : "Staff will be able to login again."}
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  marginTop: 24,
                }}
              >
                <button
                  className="btn-outline-green"
                  onClick={() =>
                    setShowConfirm({ show: false, id: null, action: null })
                  }
                >
                  Cancel
                </button>
                <button
                  className={`btn ${showConfirm.action === "delete" ? "btn-danger" : showConfirm.currentStatus ? "btn-danger" : "btn-success"}`}
                  onClick={() =>
                    showConfirm.action === "delete"
                      ? handleDelete(showConfirm.id)
                      : handleToggleStatus(showConfirm.id)
                  }
                >
                  {showConfirm.action === "delete"
                    ? "Yes, Delete"
                    : showConfirm.currentStatus
                      ? "Yes, Deactivate"
                      : "Yes, Activate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

{salaryModal && (
        <div className="modal-overlay" onClick={() => setSalaryModal(null)}>
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><BsCurrencyRupee  size={15} style={{ marginRight: 7 }} /> Set Base Salary</h3>
              <button className="modal-close" onClick={() => setSalaryModal(null)}><FiX size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: "20px 24px" }}>
              <div style={{ marginBottom: 16, padding: "10px 14px", background: "#f9fafb", borderRadius: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{salaryModal.name}</div>
                <div style={{ fontSize: 12, color: "#888" }}>{salaryModal.role} · {salaryModal.department}</div>
              </div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>
                Gross CTC (Monthly) — set once, used every month
              </label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#888" }}>₹</span>
                <input
                  type="number"
                  placeholder="e.g. 45000"
                  value={salaryInput}
                  onChange={(e) => setSalaryInput(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px 10px 28px", fontSize: 14, borderRadius: 8, border: "1.5px solid #e5e7eb", boxSizing: "border-box" }}
                  autoFocus
                />
              </div>
              {salaryInput > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: "#085041" }}>
                  Per day: ₹{(parseFloat(salaryInput) / 26).toFixed(0)} &nbsp;·&nbsp; LOP per day deducted automatically from attendance
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-outline-green" onClick={() => setSalaryModal(null)}>Cancel</button>
              <button className="btn btn-success" onClick={handleSetBaseSalary} disabled={savingSalary}>
                {savingSalary ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : <><BsCurrencyRupee size={13} /> Save Salary</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

      
  );
}